import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import Logo from "@/components/Logo";
import Img from "@/components/Img";
import { useSpotlight, STANCOR_PATH } from "@/components/SpotlightSection";

const LINKS = [
  { to: "/", key: "nav_home" },
  { to: "/about", key: "nav_about" },
  { to: "/sectors", key: "nav_sectors" },
  { to: "/joint-ventures", key: "nav_ventures" },
  { to: "/leadership", key: "nav_leadership" },
  { to: "/offices", key: "nav_offices" },
  { to: "/news", key: "nav_news" },
  { to: "/contact", key: "nav_contact" },
];

export default function Navbar() {
  const { t, L, lang, toggle } = useLang();
  const spotlight = useSpotlight("show_page");
  const site = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const name = L(site?.settings?.site_name) || "Al Qalaa International Group";
  const theme = site?.settings?.theme;
  const logoSrc =
    theme === "desert-minimalist" && site?.settings?.logo_url_light
      ? site.settings.logo_url_light
      : site?.settings?.logo_url;

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled || open ? "glass-nav" : "bg-transparent"}`}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 text-primary">
            {logoSrc ? (
              <Img
                src={logoSrc}
                width={320}
                sizes="220px"
                eager
                alt={name}
                data-testid="nav-logo-img"
                className="w-auto"
                style={{ height: `${site?.settings?.logo_height_nav || 56}px` }}
              />
            ) : (
              <>
                <Logo markSize={36} siteName={name} />
                <span className="hidden sm:block leading-tight">
                  <span className="font-head text-lg tracking-wide">{name}</span>
                  <span className="block text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Abu Dhabi — UAE</span>
                </span>
              </>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => {
              const link = (
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                  className={({ isActive }) =>
                    `text-[13px] tracking-wide transition-colors duration-300 hover:text-primary ${isActive ? "text-primary" : "text-foreground/80"}`
                  }
                >
                  {t(l.key)}
                </NavLink>
              );
              if (l.to !== "/joint-ventures" || !spotlight) return <span key={l.to}>{link}</span>;
              return (
                <div key={l.to} className="group relative">
                  {link}
                  <div className="invisible absolute start-0 top-full z-50 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                    <Link
                      to={STANCOR_PATH}
                      data-testid="nav-link-stancor"
                      className="flex items-center gap-2.5 whitespace-nowrap border border-border bg-card px-4 py-3 text-[13px] text-foreground/85 shadow-xl transition-colors hover:text-primary"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      {spotlight.logo ? <img src={spotlight.logo} alt="" className="h-5 w-5 rounded object-contain" /> : null}
                      {spotlight.name}
                    </Link>
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="lang-toggle-btn"
              onClick={toggle}
              className="h-9 px-3 border border-border rounded-full text-xs font-semibold tracking-widest hover:border-primary hover:text-primary transition-colors"
            >
              {lang === "en" ? "عربي" : "EN"}
            </button>
            <button
              type="button"
              data-testid="mobile-menu-btn"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? t("close") : t("menu")}
              className="lg:hidden h-9 px-3 border border-border rounded-full text-xs font-semibold"
            >
              {open ? t("close") : t("menu")}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-background/97 glass-nav lg:hidden flex items-center"
          >
            <nav className="px-8 flex flex-col gap-2 w-full">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.45 }}
                >
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `font-head text-4xl py-2 block ${isActive ? "text-primary" : "text-foreground"}`
                    }
                  >
                    {t(l.key)}
                  </NavLink>
                  {l.to === "/joint-ventures" && spotlight && (
                    <Link
                      to={STANCOR_PATH}
                      data-testid="mobile-nav-link-stancor"
                      className="mt-1 flex items-center gap-2 ps-5 text-base text-primary"
                    >
                      {spotlight.logo ? <img src={spotlight.logo} alt="" className="h-5 w-5 rounded object-contain" /> : null}
                      {spotlight.name}
                    </Link>
                  )}
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
